import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Designer from '../AdsManager/Designer';
import BackgroundEditor from './BackgroundEditor';
import useDeepLinkedSection from '../navigation/useDeepLinkedSection';
import DashboardSection from '../components/DashboardSection';
import './PlaylistAssign.css';
import './MediaStudioPage.css';

// Media Studio page (DJB-80/81): the Visuals > Designer feature re-homed as
// the "Ads & Screensaver Editor" section, above the "Background Editor"
// (managed backgrounds, DJB-227), both on the Dashboard's borderless
// collapsible shell (DJB-160). Each section opens and closes independently
// and both start collapsed; a collapsed header carries a muted info line
// that disappears once expanded (DJB-83).
const MediaStudioPage = () => {
  const [searchParams] = useSearchParams();

  // Deep-link target for the "Open in Studio" button (DJB-215, formerly
  // "Design") in the Ads & Screensaver Subscriptions section (DJB-81):
  // ?adId=<id> opens the editor section with
  // that ad already selected. Without a deep link everything starts collapsed.
  const adId = searchParams.get('adId');

  const [openSections, setOpenSections] = useState(() =>
    adId ? new Set(['designer']) : new Set()
  );

  const openSection = (key) =>
    setOpenSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));

  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  useEffect(() => {
    if (adId) {
      openSection('designer');
    }
  }, [adId]);

  // DJB-112: a section picked from the left nav arrives as ?section=<anchor>
  // and must open even though sections default to collapsed
  useDeepLinkedSection(
    {
      'media-studio-designer': 'designer',
      'section-background-editor': 'background',
    },
    openSection
  );

  const renderSection = ({ key, title, info, bodyId, anchorId, children }) => (
    <DashboardSection
      id={anchorId}
      title={title}
      info={info}
      isOpen={openSections.has(key)}
      onToggle={() => toggleSection(key)}
      bodyId={bodyId}
    >
      {children}
    </DashboardSection>
  );

  return (
    <div className="ps-page ps-page--media-studio">
      {renderSection({
        key: 'designer',
        title: 'Ads & Screensaver Editor',
        info: 'Upload nieuwe advertenties en screensavers en stel hun eigenschappen in.',
        bodyId: 'ps-body-designer-media-studio',
        // Stable anchor for links straight to the editor section
        anchorId: 'media-studio-designer',
        // Keyed on the deep-linked ad so a new ?adId always (re)selects it,
        // even if the Designer was already mounted
        children: <Designer key={adId ?? 'designer'} initialAdId={adId} />,
      })}
      {renderSection({
        key: 'background',
        title: 'Background Editor',
        info: 'Maak achtergronden voor het hoofdscherm aan en beheer ze.',
        bodyId: 'ps-body-background-media-studio',
        anchorId: 'section-background-editor',
        children: <BackgroundEditor />,
      })}
    </div>
  );
};

export default MediaStudioPage;
